import { useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import useScrollReveal from '@/hooks/useScrollReveal';
import contactHero from '@/assets/contact-hero.jpg';

const Contact = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  useScrollReveal(pageRef);

  return (
    <div className="font-jakarta bg-white">
      <Navbar />
      <main ref={pageRef}>

        {/* Page Header */}
        <section className="pt-14 bg-white">
          <div className="max-w-4xl mx-auto px-6 md:px-10 pt-14 pb-12 text-center">
            <div data-reveal="fade" data-delay="1" className="mb-4">
              <span className="text-[11px] font-bold text-bteal tracking-widest uppercase">Get In Touch</span>
            </div>
            <h1 data-reveal data-delay="2" className="text-4xl md:text-5xl font-bold text-ink leading-tight tracking-tight mb-4">
              Contact Bihar Ethanol Association
            </h1>
            <p data-reveal data-delay="3" className="text-ink-muted text-lg max-w-lg mx-auto leading-relaxed">
              Connect with us to inquire about membership, sell your produce, set up an ethanol plant, explore partnership proposals, or request policy and media details.
            </p>
          </div>
          <div className="max-w-7xl mx-auto px-6 md:px-10 pb-10">
            <div data-reveal="scale" data-delay="4" className="rounded-2xl overflow-hidden w-full shadow-lg" style={{ height: '300px' }}>
              <img
                src={contactHero}
                alt="Contact Bihar Ethanol Association"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          <div className="h-px bg-gray-100" />
        </section>

        {/* Contact Content */}
        <section className="py-14 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">

              {/* Left Column: Contact Details */}
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <span data-reveal="left" data-delay="1" className="text-[11px] font-bold text-bgreen tracking-widest uppercase block">Contact Details</span>
                  <h2 data-reveal="left" data-delay="1" className="text-xl font-bold text-ink mt-1">Bihar Ethanol Association</h2>

                  <div className="mt-5 space-y-0">

                    {/* Address */}
                    <div data-reveal="left" data-delay="2" className="flex items-start gap-3.5 py-4 border-b border-gray-100">
                      <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-bgreen/10 text-bgreen-dark flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-2-line text-sm" />
                      </span>
                      <div>
                        <p className="text-[10px] text-ink-muted font-bold uppercase tracking-wider mb-1">Registered Address</p>
                        <p className="text-sm font-semibold text-ink leading-relaxed">
                          C-0503, City Centre Mall, Opposite Fire Station, Buddh Marg, Patna, Bihar 800001
                        </p>
                        <a
                          href="https://maps.app.goo.gl/7uka9T8jwwKWX4Uz8"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-bold text-bteal hover:underline mt-2"
                        >
                          <i className="ri-map-2-line text-xs" />
                          <span>View on Google Maps</span>
                          <i className="ri-arrow-right-up-line text-xs" />
                        </a>
                      </div>
                    </div>

                    {/* Email */}
                    <div data-reveal="left" data-delay="3" className="flex items-start gap-3.5 py-4 border-b border-gray-100">
                      <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-bteal/10 text-bteal flex-shrink-0 mt-0.5">
                        <i className="ri-mail-line text-sm" />
                      </span>
                      <div>
                        <p className="text-[10px] text-ink-muted font-bold uppercase tracking-wider mb-1">Email Addresses</p>
                        <div className="space-y-1">
                          <a href="mailto:ethanolassociationbihar@gmail.com" className="text-sm font-semibold text-bteal hover:underline block truncate">
                            ethanolassociationbihar@gmail.com
                          </a>

                        </div>
                      </div>
                    </div>

                    {/* Landline */}
                    <div data-reveal="left" data-delay="4" className="flex items-start gap-3.5 py-4 border-b border-gray-100">
                      <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-bteal/10 text-bteal flex-shrink-0 mt-0.5">
                        <i className="ri-phone-line text-sm" />
                      </span>
                      <div>
                        <p className="text-[10px] text-ink-muted font-bold uppercase tracking-wider mb-1">Landline Phone</p>
                        <a href="tel:06122330122" className="text-sm font-semibold text-ink hover:text-bteal">
                          +91 612 2330122
                        </a>
                      </div>
                    </div>

                    {/* Mobile */}
                    <div data-reveal="left" data-delay="5" className="flex items-start gap-3.5 py-4 border-b border-gray-100">
                      <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-bgreen/10 text-bgreen-dark flex-shrink-0 mt-0.5">
                        <i className="ri-smartphone-line text-sm" />
                      </span>
                      <div>
                        <p className="text-[10px] text-ink-muted font-bold uppercase tracking-wider mb-1">Mobile Contact</p>
                        <a href="tel:916201270234" className="text-sm font-semibold text-ink hover:text-bteal">
                          +91 62012 70234
                        </a>
                      </div>
                    </div>

                    {/* Hours */}
                    <div data-reveal="left" data-delay="6" className="flex items-start gap-3.5 py-4">
                      <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-amber-100/80 text-amber-900 flex-shrink-0 mt-0.5">
                        <i className="ri-time-line text-sm" />
                      </span>
                      <div>
                        <p className="text-[10px] text-ink-muted font-bold uppercase tracking-wider mb-1">Office Hours</p>
                        <p className="text-sm font-semibold text-ink">Mon–Fri: 10:00 AM – 5:00 PM IST</p>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Social Connect */}
                <div data-reveal="left" data-delay="7">
                  <p className="text-[11px] text-ink font-bold uppercase tracking-wider mb-3">Connect With BEA</p>
                  <div className="flex items-center gap-3">
                    {[
                      { label: 'Facebook', icon: 'ri-facebook-fill', href: 'https://www.facebook.com/biharethanol', style: 'bg-[#1877F2]/10 text-[#1877F2] hover:bg-[#1877F2] hover:text-white border-[#1877F2]/20' },
                      { label: 'LinkedIn', icon: 'ri-linkedin-fill', href: 'https://www.linkedin.com/company/biharethanol/', style: 'bg-[#0A66C2]/10 text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white border-[#0A66C2]/20' },
                      { label: 'X (Twitter)', icon: 'ri-twitter-x-fill', href: 'https://x.com/biharethanol', style: 'bg-black/5 text-black hover:bg-black hover:text-white border-black/10' },
                    ].map((s) => (
                      <a
                        key={s.label}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={s.label}
                        className={`w-11 h-11 rounded-xl border flex items-center justify-center transition-all duration-200 cursor-pointer shadow-2xs hover:scale-105 ${s.style}`}
                      >
                        <i className={`${s.icon} text-xl`} />
                      </a>
                    ))}
                  </div>
                </div>

              </div>

              {/* Right Column: Google Maps */}
              <div className="lg:col-span-3 flex flex-col">
                <div data-reveal="right" data-delay="2" className="flex-1 rounded-2xl overflow-hidden border border-gray-200 shadow-xs min-h-[420px] relative">
                  <iframe
                    src="https://maps.google.com/maps?q=City+Centre+Mall+Buddh+Marg+Patna+Bihar+800001&t=&z=16&ie=UTF8&iwloc=&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0, minHeight: '420px' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Bihar Ethanol Association Office Location - City Centre Mall Patna"
                  />
                  <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md p-4 rounded-xl border border-gray-200/80 shadow-md flex items-center justify-between gap-4">
                    <div>
                      <h4 className="font-bold text-ink text-xs sm:text-sm">BEA Registered Headquarters</h4>
                      <p className="text-[11px] text-ink-muted mt-0.5">C-0503, City Centre Mall, Buddh Marg, Patna</p>
                    </div>
                    <a
                      href="https://maps.app.goo.gl/7uka9T8jwwKWX4Uz8"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3.5 py-2 rounded-lg bg-bteal text-white text-xs font-semibold hover:bg-bteal-dark transition-all whitespace-nowrap shrink-0 shadow-xs flex items-center gap-1.5"
                    >
                      <span>Get Directions</span>
                      <i className="ri-arrow-right-up-line text-xs" />
                    </a>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
